import {
  ASSIGNABLE,
  DEPARTED,
  NOT_READY,
  PENDING_DEPART,
  PENDING_PICK,
  PENDING_STAGE,
  ROUTES_AT_RISK,
  ROUTES_TO_BE_CUT,
} from "../RouteStatusManager";
import { AR, DEFAULT_REFRESH_INTERVAL, SSD, FEATURES } from "./Constants";
import { getFeatureStatus } from "../../handler/ParentDataHandler";

export function getTimeDiffInSecondsFromCurrentTimeInHHMMSSFormat(time) {
  if (!time) {
    return null;
  }
  const curDate = new Date();
  const timeDiff = curDate.getTime() - time;
  return convertMillisToSecond(timeDiff);
}

export function isTimeValid(time) {
  return (time !== null) & (time !== 0);
}

/**
 * Returns count of possible routes which meet the to-be-cut criteria
 * @param routeList
 * @param rcTimeLimit
 * @param rcPackageCount
 * @returns {number|*}
 */
export function filterRouteCut(routeList, rcTimeLimit, rcPackageCount) {
  let toBeCutCount = 0;
  routeList.forEach((route) => {
    let packagesPendingToBePicked =
      route.rswsPickProgress.totalCount - route.rswsPickProgress.completedCount;
    let minutesSinceLastPicked =
      route.rswsPickProgress.lastCompletionTime !== undefined
        ? (Date.now() - route.rswsPickProgress.lastCompletionTime) / 60000
        : -1;
    //All such routes for which there hasn’t been a pick in the last X mins (>= X) and the packages pending to be picked are Y (<= Y)
    if (
      minutesSinceLastPicked !== undefined &&
      minutesSinceLastPicked >= rcTimeLimit &&
      packagesPendingToBePicked <= rcPackageCount &&
      route.overviewStatus === PENDING_PICK
    ) {
      ++toBeCutCount;
    }
  });
  return toBeCutCount;
}

export function getPickTimeToBeDisplayed(route) {
  if (route.pickAssociates.length > 0) {
    return route.pickAssociates[0].pickingDuration != null
      ? convertSecondsToTimeFormat(
          convertMillisToSecond(route.pickAssociates[0].pickingDuration),
        )
      : route.pickAssociates[0].startTime != null
        ? getTimeDiffFromCurrentTimeInHHMMSSFormat(
            route.pickAssociates[0].startTime,
          )
        : "...";
  } else {
    return "...";
  }
}

export function getTimeDiffFromCurrentTimeInHHMMSSFormat(time) {
  if (!time) {
    return "...";
  }
  const curDate = new Date();
  const timeDiff = curDate.getTime() - time;
  return convertSecondsToTimeFormat(timeDiff / 1000);
}

export function convertMillisToSecond(time) {
  return Math.ceil(time / 1000);
}

export function getDateInYYYYMMDDFormat(date) {
  if (!date) {
    return null;
  }
  const month = ("0" + (date.getMonth() + 1).toString()).slice(-2);
  const day = ("0" + date.getDate().toString()).slice(-2);
  return `${date.getFullYear()}-${month}-${day}`;
}

export function convertDateInDateTimestampFormat(date) {
  if (!date) {
    return null;
  }
  return `${getDateInYYYYMMDDFormat(date)}T${convertSecondsToHHMM(date.getTime())}`;
}

export function getLabelsFromDataPoint(dataPointList) {
  return dataPointList.map(
    (dataPoint) =>
      new Date(dataPoint.timeStampVal)
        .toTimeString()
        .split(" ")[0]
        .split(":")[0] +
      ":" +
      new Date(dataPoint.timeStampVal)
        .toTimeString()
        .split(" ")[0]
        .split(":")[1],
  );
}

export function getTimeLabelFromDataPoint(dataPoint) {
  if (dataPoint) {
    return (
      new Date(dataPoint.timeStampVal)
        .toTimeString()
        .split(" ")[0]
        .split(":")[0] +
      ":" +
      new Date(dataPoint.timeStampVal)
        .toTimeString()
        .split(" ")[0]
        .split(":")[1]
    );
  }
  return null;
}

export function getLabelsFromDataPointV2(dataPointList, additionalDatesList) {
  let newDataPointList = [...dataPointList];
  if (additionalDatesList) {
    const newAdditionalDatesList = additionalDatesList.map(
      convertDateInDateTimestampFormat,
    );
    const additionalDataPointList = newAdditionalDatesList.map(
      (dateTimeStamp) => ({ timeStampVal: dateTimeStamp }),
    );
    newDataPointList.push(...additionalDataPointList);
    newDataPointList = removeDuplicates(newDataPointList, "timeStampVal");
    newDataPointList.sort(function (a, b) {
      return new Date(a.timeStampVal) - new Date(b.timeStampVal);
    });
  }
  return getLabelsFromDataPoint(newDataPointList);
}

export function convertSecondsToTimeFormat(seconds) {
  const hours = Math.floor(seconds / 3600) % 24;
  const minutes = Math.floor(seconds / 60) % 60;
  const secs = Math.round(seconds % 60);
  return [hours, minutes, secs]
    .map((v) => (v < 10 ? "0" + v : v))
    .filter((v, i) => v !== "00" || i > 0)
    .join(":");
}

export function removeDuplicates(list, property) {
  return list.filter((obj, position, listOfOne) => {
    return (
      listOfOne.map((mapObj) => mapObj[property]).indexOf(obj[property]) ===
      position
    );
  });
}

export function convertSecondsToHHMM(milliseconds) {
  const time = new Date(parseInt(milliseconds, 10));
  return (
    ("0" + time.getHours()).slice(-2) +
    ":" +
    ("0" + time.getMinutes()).slice(-2)
  );
}

export function getDateInMMDDYYYYWithOffset(offset) {
  let presentDate = new Date();
  presentDate.setDate(presentDate.getDate() + offset);
  return (
    presentDate.getMonth() +
    1 +
    "/" +
    presentDate.getDate() +
    "/" +
    presentDate.getFullYear()
  );
}

export function getBaseUrl(data) {
  const url = new URL(data);
  return url.origin;
}

export function getRouteByStatusCount(status, routeList) {
  let result = 0;
  if (status === "Picked") {
    routeList.forEach((route) => {
      if (route.status === "Assignable" || route.status === "In Progress") {
        result = result + 1;
      }
    });
  } else if (status === "Sidelined") {
    routeList.forEach((route) => {
      if (route.status === "Sidelined") {
        result = result + 1;
      }
    });
  } else if (status === "Staged") {
    routeList.forEach((route) => {
      if (route.status !== "Sidelined") {
        let preStagedCart = 0;
        route.carts.forEach((cart) => {
          if (cart.preStagedTime) {
            preStagedCart++;
          }
        });
        if (preStagedCart === route.carts.length) {
          result = result + 1;
        }
      }
    });
  } else if (status === "Pending Departed") {
    routeList.forEach((route) => {
      if (
        route.status !== "Departed" &&
        route.status !== "Departed (partial)" &&
        route.status !== "Sidelined"
      ) {
        result = result + 1;
      }
    });
  } else if (status === "Departed") {
    routeList.forEach((route) => {
      if (
        route.status === "Departed" ||
        route.status === "Departed (partial)"
      ) {
        result = result + 1;
      }
    });
  }
  return result;
}

export function parseCapabilitiesResponse(response) {
  return response ? JSON.parse(response.capability) : "";
}

export function formatAlias(alias) {
  return alias != null && alias.indexOf("@") > -1 ? alias.split("@")[0] : alias;
}

/**
 * Compare 2 arrays of string and returns true if they are equal.
 * @param array1
 * @param array2
 * @return {boolean}
 */
export function isArrayEquals(array1, array2) {
  if (!array1 || !array2 || array1.length !== array2.length) {
    return false;
  }
  const sortedArray1 = array1.sort();
  const sortedArray2 = array2.sort();
  for (let i = sortedArray1.length; i--; ) {
    if (sortedArray1[i] !== sortedArray2[i]) return false;
  }
  return true;
}

export function getTargetTabByMetric(metric, capability) {
  if (metric === DEPARTED || metric === PENDING_DEPART) {
    return "depart";
  } else if (
    metric === PENDING_STAGE ||
    (isARorSSD(capability) && metric === ASSIGNABLE)
  ) {
    return "stage";
  } else if (
    metric === PENDING_PICK ||
    metric === ROUTES_AT_RISK ||
    metric === ASSIGNABLE ||
    metric === ROUTES_TO_BE_CUT
  ) {
    return "pick";
  } else if (metric === NOT_READY) {
    return "stow";
  } else {
    return null;
  }
}

/**
 * Check if a station is of program type AR/SSD
 * @param capability
 * @returns {*|boolean}
 */
export function isARorSSD(capability) {
  return capability && (capability === AR || capability === SSD);
}

/**
 * Return cluster from route code.
 * Supports routing 3.0 regex.
 *
 * @param routeCode
 * @return {*}
 */
export function getCluster(routeCode) {
  if (!routeCode) {
    return "";
  }
  return routeCode.split("-")[0].replace(/[0-9]/g, "");
}

/**
 * This method return the refresh interval in milliseconds
 * @returns {number}
 */
export function getRefreshIntervalForOutbound(stationConfig) {
  if (stationConfig) {
    return stationConfig.refreshInterval;
  }
  return DEFAULT_REFRESH_INTERVAL;
}

/**
 * This method returns the list of route codes in "not ready" state given a list of routes
 * @param routeList List of routes from which to filter
 * @returns {string|*} List of route codes in not ready state
 */
export function getRouteCodeListForStow(routeList) {
  if (!Array.isArray(routeList)) {
    return "";
  }
  return routeList
    .filter((route) => route.overviewStatus === NOT_READY)
    .map((route) => route.routeCode)
    .join(",");
}

/**
 * Checks if the RSWS Boson Flow feature is enabled.
 *
 * @returns {Promise<boolean>} A Promise that resolves to a boolean indicating whether the feature is enabled.
 */
export async function isRSWSBosonFlowEnabled() {
  try {
    const response = await getFeatureStatus([
      FEATURES.IS_RSWS_BOSON_FLOW_ENABLED,
    ]);
    return response.data && response.data.isRSWSBosonFlowEnabled;
  } catch (ex) {
    console.error("Error occurred while fetching config from Boson App config");
  }
  // Return false if there's an error or if the feature is not enabled.
  return false;
}



// WEBPACK FOOTER //
// ./src/components/outboundAmzl/util/Util.js